import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { FloppyDisk, Printer } from "@phosphor-icons/react";
import DataMigrationPanel from "@/components/DataMigrationPanel";
import BookingsMigrationPanel from "@/components/BookingsMigrationPanel";
import BackfillDealerCodePanel from "@/components/BackfillDealerCodePanel";
import BulkSetDealerCodeByVinPanel from "@/components/BulkSetDealerCodeByVinPanel";
import BulkMoveVehiclesPanel from "@/components/BulkMoveVehiclesPanel";
import WhatsAppTemplatesPanel from "@/components/WhatsAppTemplatesPanel";
import PlatformsPanel from "@/components/PlatformsPanel";
import { getAutoPrintReceipts, setAutoPrintReceipts } from "@/lib/paymentReceipt";
import { LogoUploader, resolveLogoUrl } from "@/components/LogoUploader";

const EMPTY = {
  name: "",
  address: "",
  state_name: "",
  state_code: "",
  gstin: "",
  phone: "",
  email: "",
  logo_url: "",
};

function Field({ label, value, onChange, testId, placeholder, area }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
        {label}
      </label>
      {area ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          rows={3}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoPrint, setAutoPrint] = useState(false);

  useEffect(() => {
    setAutoPrint(getAutoPrintReceipts());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/dealership");
        if (!cancelled) setForm({ ...EMPTY, ...data });
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/dealership", form);
      setForm({ ...EMPTY, ...data });
      toast.success("Dealership settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="08 / Settings"
        title="Dealership Settings"
        subtitle="Shown on every printable invoice and the 3-receipt RTO export. Admin only."
      />

      <form onSubmit={onSubmit} className="px-8 py-6 max-w-3xl space-y-4">
        {loading ? (
          <div className="font-mono text-xs text-ink-subtle">Loading…</div>
        ) : (
          <>
            <Field
              label="Dealership Name"
              value={form.name}
              onChange={set("name")}
              testId="settings-name"
              placeholder="e.g. AMARAVATI AUTOMOTIVES PVT LTD"
            />
            <Field
              label="Address"
              value={form.address}
              onChange={set("address")}
              testId="settings-address"
              area
              placeholder="#5-1-60, KRISHNAMURTHY STREET, SURYARAOPETA, KAKINADA, ANDHRA PRADESH - 533001"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="State Name"
                value={form.state_name}
                onChange={set("state_name")}
                testId="settings-state-name"
                placeholder="ANDHRA PRADESH"
              />
              <Field
                label="State Code"
                value={form.state_code}
                onChange={set("state_code")}
                testId="settings-state-code"
                placeholder="37"
              />
            </div>
            <Field
              label="GSTIN"
              value={form.gstin}
              onChange={set("gstin")}
              testId="settings-gstin"
              placeholder="37AAYCA9974G1ZO"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Phone"
                value={form.phone}
                onChange={set("phone")}
                testId="settings-phone"
              />
              <Field
                label="Email"
                value={form.email}
                onChange={set("email")}
                testId="settings-email"
              />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Field
                  label="Logo URL (or Google Drive share link)"
                  value={form.logo_url}
                  onChange={set("logo_url")}
                  testId="settings-logo-url"
                  placeholder="https://… or upload a file →"
                />
              </div>
              <LogoUploader onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))} />
            </div>
            {form.logo_url && (
              <div className="border border-canvas-line p-3 bg-canvas-panel">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
                  Logo preview
                </div>
                <img
                  src={resolveLogoUrl(form.logo_url)}
                  alt="Logo preview"
                  data-testid="settings-logo-preview"
                  className="max-h-20 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            )}

            {/* Receipt printing preferences — per-browser, not synced to server. */}
            <div className="border border-canvas-line p-4 bg-canvas-panel space-y-2">
              <div className="flex items-center gap-2">
                <Printer size={14} weight="bold" className="text-ink-muted" />
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                  Receipt printing
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-auto-print-row">
                <input
                  type="checkbox"
                  checked={autoPrint}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setAutoPrint(v);
                    setAutoPrintReceipts(v);
                    toast.success(v ? "Auto-print enabled on this device" : "Auto-print disabled on this device");
                  }}
                  data-testid="settings-auto-print"
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold">Auto-print payment receipts after collection</div>
                  <div className="text-xs text-ink-muted">
                    When on, the thermal receipt fires the browser print dialog automatically after reconciling a due
                    or recording a payment. Stored per-browser.
                  </div>
                </div>
              </label>
            </div>

            <div className="pt-4 border-t border-canvas-line flex justify-end">
              <button
                type="submit"
                disabled={saving}
                data-testid="settings-save"
                className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60 flex items-center gap-2"
              >
                <FloppyDisk size={14} weight="bold" />
                {saving ? "Saving…" : "Save settings"}
              </button>
            </div>
          </>
        )}
      </form>

      {!loading && (
        <div className="px-8 pb-12 max-w-4xl space-y-3">
          <div className="border-t-2 border-canvas-line pt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              09 / Data Migration
            </div>
            <h2 className="font-display text-2xl font-bold text-ink mt-1">
              Historical Data Import
            </h2>
            <p className="text-sm text-ink-muted mt-1">
              One-time tool to wipe demo data and bulk-import 5 years of sales from your Excel exports.
            </p>
          </div>
          <DataMigrationPanel />
          <BookingsMigrationPanel />
          <BackfillDealerCodePanel />
          <BulkSetDealerCodeByVinPanel />
          <BulkMoveVehiclesPanel />
          <WhatsAppTemplatesPanel />
          <PlatformsPanel />
        </div>
      )}
    </div>
  );
}
